import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import { formatNaira } from "../../components/Billing/UpgradeBanner";
import "../../components/Billing/Billing.css";

const PERKS = [
  "Every subject, every topic, for JSS1 to SS3",
  "Lessons with labelled diagrams, a quiz after every chapter",
  "\"Explain it differently\" and practice on the questions you missed",
  "Cancel whenever you like. You keep access until the month you paid for ends",
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "");
const msg = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function Subscribe() {
  const [params] = useSearchParams();
  const from = params.get("from");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/api/billing/status")
      .then((r) => setStatus(r.data))
      .catch((e) => setError(msg(e, "We couldn't load your subscription. Please try again.")))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const subscribe = async () => {
    setBusy(true);
    setError("");
    try {
      // remember where to send them after paying (only paths inside this site)
      if (from && from.startsWith("/") && !from.startsWith("//")) sessionStorage.setItem("billing_return", from);
      const { data } = await api.post("/api/billing/checkout");
      window.location.href = data.authorization_url;     // Paystack's secure checkout page
    } catch (e) {
      setError(msg(e, "We couldn't start the payment. Please try again."));
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/billing/cancel");
      setStatus(data);
      setConfirmCancel(false);
    } catch (e) {
      setError(msg(e, "We couldn't cancel right now. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const sub = status?.subscription;
  const price = formatNaira(status?.price_ngn ?? 2000);
  const back = from && from.startsWith("/") && !from.startsWith("//") ? from : "/courses";

  return (
    <Layout>
      <div className="bl-page">
        <section className="bl-hero">
          <h1>{sub?.active ? "Thank you for subscribing" : "Unlock every course"}</h1>
          <p>{sub?.active ? "You have full access to every subject." : "The first topic of every subject is free. Subscribe to unlock the rest."}</p>
          {!sub?.active && !status?.exempt && (
            <div className="bl-price"><strong>{price}</strong><span>per month</span></div>
          )}
        </section>

        {loading && <div className="bl-center" aria-busy="true"><div className="bl-spinner" /></div>}
        {error && <div className="bl-alert" role="alert">{error}</div>}

        {!loading && status?.exempt && (
          <section className="bl-card">
            <h2>You already have full access</h2>
            <p>Teacher and admin accounts don't need a subscription.</p>
            <div className="bl-actions"><Link to="/courses" className="bl-btn bl-btn-gold">Go to courses</Link></div>
          </section>
        )}

        {!loading && status && !status.exempt && (
          <>
            {sub?.active ? (
              <section className="bl-card">
                <span className={`bl-status ${sub.cancel_at_period_end ? "ending" : ""}`}>
                  <Check size={16} aria-hidden="true" /> {sub.cancel_at_period_end ? "Ending" : "Active"}
                </span>
                <p style={{ marginTop: 12 }}>
                  {sub.cancel_at_period_end
                    ? `You won't be charged again. You keep full access until ${fmtDate(sub.renews_at)}.`
                    : sub.will_renew
                    ? `Your subscription renews on ${fmtDate(sub.renews_at)} (${price}).`
                    : `You have access until ${fmtDate(sub.renews_at)}. Renew any time to keep going.`}
                </p>
                {sub.payment_problem && (
                  <div className="bl-warn" role="alert">
                    Your last renewal payment didn't go through. Please check your card with your bank, or subscribe again below.
                  </div>
                )}
                <div className="bl-actions">
                  <Link to={back} className="bl-btn bl-btn-gold">Continue learning</Link>
                  {(!sub.will_renew || sub.payment_problem) && (
                    <button type="button" className="bl-btn bl-btn-outline" onClick={subscribe} disabled={busy}>
                      {busy ? "Opening Paystack…" : `Pay ${price} for another month`}
                    </button>
                  )}
                  {sub.will_renew && !confirmCancel && (
                    <button type="button" className="bl-btn bl-btn-outline" onClick={() => setConfirmCancel(true)}>
                      Cancel subscription
                    </button>
                  )}
                </div>
                {confirmCancel && (
                  <div className="bl-warn" role="alertdialog" aria-label="Confirm cancellation">
                    <p style={{ marginTop: 0 }}>
                      Cancel your subscription? You won't be charged again and you keep access until {fmtDate(sub.renews_at)}.
                    </p>
                    <div className="bl-actions" style={{ marginTop: 8 }}>
                      <button type="button" className="bl-btn bl-btn-danger" onClick={cancel} disabled={busy}>
                        {busy ? "Cancelling…" : "Yes, cancel it"}
                      </button>
                      <button type="button" className="bl-btn bl-btn-outline" onClick={() => setConfirmCancel(false)} disabled={busy}>
                        Keep my subscription
                      </button>
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <section className="bl-card">
                <h2>What you get</h2>
                <ul className="bl-list">
                  {PERKS.map((p) => (
                    <li key={p}><span className="bl-tick"><Check size={14} aria-hidden="true" /></span><span>{p}</span></li>
                  ))}
                </ul>
                {sub && !sub.active && (
                  <div className="bl-warn">Your subscription ended on {fmtDate(sub.renews_at)}. Subscribe again to get everything back.</div>
                )}
                <div className="bl-actions">
                  <button type="button" className="bl-btn bl-btn-gold" onClick={subscribe} disabled={busy || !status.configured}>
                    {busy ? "Opening Paystack…" : `Subscribe for ${price} a month`}
                  </button>
                  <Link to={back} className="bl-btn bl-btn-outline">Not now</Link>
                </div>
                {!status.configured && <p className="bl-note">Payments are not available yet. Please check back soon.</p>}
                <p className="bl-note">
                  <ShieldCheck size={14} aria-hidden="true" style={{ verticalAlign: "-2px" }} /> Payment is handled by Paystack. We never see or store your card details.
                  {status.recurring ? " Your card is charged again each month until you cancel." : ""}
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
